/**
 * NO03 - Fakturakopi Search Component
 * Search form with advanced search options
 */

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Collapse,
  Typography,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Search as SearchIcon, ExpandMore, ExpandLess } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/nb'; // Norwegian Bokmål locale
import { makeStyles } from 'tss-react/mui';
import { AppState } from '../../../store/rootReducer';
import * as actionsNO03 from '../../../store/actions/indexNO03';
import { NO03SearchParams } from '../../../models/NO03';
import FirmNavnId from '../../../models/FirmNavnId';

const useStyles = makeStyles()((theme) => ({
  searchContainer: {
    padding: theme.spacing(3),
    margin: theme.spacing(2),
    backgroundColor: '#fff',
  },
  title: {
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    fontSize: '1.5rem',
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    fontSize: '0.875rem',
  },
  firmaSelectBox: {
    marginBottom: theme.spacing(2),
    maxWidth: '400px',
    '& .MuiOutlinedInput-root': {
      height: '40px',
    },
    '& .MuiInputBase-input': {
      padding: '8px 14px',
    },
  },
  quickSearchWrapper: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    maxWidth: '500px',
  },
  quickSearchField: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f5f5f5',
      height: '40px',
    },
    '& .MuiInputBase-input': {
      padding: '8px 14px',
    },
  },
  searchButton: {
    minWidth: '80px',
    height: '40px',
    textTransform: 'none',
    fontSize: '0.95rem',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  advancedToggle: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(2),
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  advancedSection: {
    backgroundColor: '#f9f9f9',
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  dateField: {
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-root': {
      height: '40px',
    },
    '& .MuiInputBase-input': {
      padding: '8px 14px',
    },
  },
}));

export default function NO03Search() {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Internal UI state - more flexible for user input
  const [searchState, setSearchState] = useState({
    companyId: '998', // 3-character company ID
    quickSearch: '', // Quick search - will be mapped to invoiceNumber
    customerNumber: '', // Can be string in UI, converted to number
    amount: '', // Can be string in UI, converted to decimal
    fromDate: '', // yyyy-MM-dd format
    toDate: '', // yyyy-MM-dd format
    orderNumber: '', // For advanced search
  });

  // Hardcoded firma list for testing - only Vitec Autodata
  const firmaList: FirmNavnId[] = [
    new FirmNavnId(998, 'Vitec Autodata', 0, 'VA'),
  ];

  const selectedFirma = useSelector((state: AppState) => state.NO03.selectedFirma);
  const loading = useSelector((state: AppState) => state.NO03.loading);

  const handleInputChange = (field: string, value: any) => {
    setSearchState({
      ...searchState,
      [field]: value,
    });
  };

  const handleFirmaChange = (firmaId: number) => {
    const firma = firmaList.find((f) => f.firmID === firmaId);
    if (firma) {
      dispatch(actionsNO03.NO03SetFirma(firma));
      // Convert firma ID to 3-character string
      const companyIdStr = firmaId.toString().padStart(3, '0');
      setSearchState({
        ...searchState,
        companyId: companyIdStr,
      });
    }
  };

  const handleSearch = () => {
    // Map UI state to backend contract
    const params: NO03SearchParams = {
      companyId: searchState.companyId,
    };

    // Quick search - map to invoice number for now
    if (searchState.quickSearch) {
      params.invoiceNumber = searchState.quickSearch;
    }

    // Customer number - convert to number if valid
    if (searchState.customerNumber) {
      const custNum = parseInt(searchState.customerNumber, 10);
      if (!isNaN(custNum)) {
        params.customerNumber = custNum;
      }
    }

    // Amount - convert to number if valid
    if (searchState.amount) {
      const amountNum = parseFloat(searchState.amount);
      if (!isNaN(amountNum)) {
        params.amount = amountNum;
      }
    }

    // Date range
    if (searchState.fromDate) {
      params.fromDate = searchState.fromDate;
    }
    if (searchState.toDate) {
      params.toDate = searchState.toDate;
    }

    // Order number (advanced search)
    if (searchState.orderNumber) {
      params.orderNumber = searchState.orderNumber;
    }

    dispatch(actionsNO03.NO03SearchRequest(params));
  };

  const handleClear = () => {
    setSearchState({
      companyId: searchState.companyId, // Keep company ID
      quickSearch: '',
      customerNumber: '',
      amount: '',
      fromDate: '',
      toDate: '',
      orderNumber: '',
    });
    dispatch(actionsNO03.NO03ClearResults());
  };

  return (
    <Paper className={classes.searchContainer} elevation={2}>
      <Typography variant="h5" className={classes.title}>
        Fakturakopi
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        Søk og skriv ut fakturakopier
      </Typography>

      {/* Firma Selection */}
      <Box className={classes.firmaSelectBox}>
        <FormControl fullWidth size="small">
          <InputLabel>Valg firma</InputLabel>
          <Select
            value={parseInt(searchState.companyId, 10) || ''}
            onChange={(e) => handleFirmaChange(Number(e.target.value))}
            label="Valg firma"
          >
            <MenuItem value="">
              <em>Velg firma</em>
            </MenuItem>
            {firmaList.map((firma) => (
              <MenuItem key={firma.firmID} value={firma.firmID}>
                {firma.firmName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Quick Search with button inline */}
      <Box className={classes.quickSearchWrapper}>
        <TextField
          fullWidth
          size="small"
          placeholder="Søk på fakturanummer"
          value={searchState.quickSearch}
          onChange={(e) => handleInputChange('quickSearch', e.target.value)}
          className={classes.quickSearchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<SearchIcon fontSize="small" />}
          onClick={handleSearch}
          disabled={loading}
          className={classes.searchButton}
        >
          Søk
        </Button>
      </Box>

      {/* Advanced Search Toggle */}
      <Box
        className={classes.advancedToggle}
        onClick={() => setAdvancedOpen(!advancedOpen)}
      >
        {advancedOpen ? <ExpandLess /> : <ExpandMore />}
        <Typography variant="body2" sx={{ ml: 0.5 }}>
          {advancedOpen ? 'Tilbake til hurtigsøk' : 'Avansert søk'}
        </Typography>
      </Box>

      {/* Advanced Search Section */}
      <Collapse in={advancedOpen}>
        <Box className={classes.advancedSection}>
          <Grid container spacing={2}>
            {/* Row 1: Customer Number */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Kundenummer"
                placeholder="F.eks. 500190"
                value={searchState.customerNumber}
                onChange={(e) => handleInputChange('customerNumber', e.target.value)}
                className={classes.dateField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Row 2: Amount */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Søk på beløp"
                placeholder="F.eks. 10999"
                value={searchState.amount}
                onChange={(e) => handleInputChange('amount', e.target.value)}
                className={classes.dateField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Row 3: Order Number */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Ordrenummer"
                placeholder="F.eks. 123456"
                value={searchState.orderNumber}
                onChange={(e) => handleInputChange('orderNumber', e.target.value)}
                className={classes.dateField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Row 4: Date Range - Two columns */}
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                <DatePicker
                  label="Fra dato"
                  format="DD.MM.YYYY"
                  value={searchState.fromDate ? dayjs(searchState.fromDate) : null}
                  onChange={(date: Dayjs | null) => {
                    handleInputChange('fromDate', date ? date.format('YYYY-MM-DD') : '');
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      placeholder: 'Velg dato',
                      className: classes.dateField,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                <DatePicker
                  label="Til dato"
                  format="DD.MM.YYYY"
                  value={searchState.toDate ? dayjs(searchState.toDate) : null}
                  onChange={(date: Dayjs | null) => {
                    handleInputChange('toDate', date ? date.format('YYYY-MM-DD') : '');
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      placeholder: 'Velg dato',
                      className: classes.dateField,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={handleClear}>
                  Nullstill
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={loading}
                >
                  Søk
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
}
